import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';

import actions from './actions';
import state from './state';
import mutations from './mutations';
import getters from './getters';
import modules from './modules';

Vue.use(Vuex);

// vuex-persistedstate subscribes to *every* commit and, for each one, deep-picks the paths
// below, JSON.stringifies them and does a synchronous localStorage.setItem. The persisted
// blob includes every Plex server with all its libraries, so that is not free -- and during
// playback the commit rate is high while almost none of it touches a persisted path:
//   - slplayer/UPDATE_PLAYER_CONTROLS_SHOWN fires every 250ms
//   - plexclients timeline mutations fire every 1s
//   - synclounge user-state mutations fire once per remote user per update
// The `filter` below gates the pick and the write, not just the write.
//
// This is a denylist rather than an allowlist on purpose: a mutation added later defaults
// to being persisted (correct but slower) instead of silently not being persisted. Every
// entry here has been checked to write only to non-persisted state.
const hotNonPersistedMutations = new Set([
  'slplayer/UPDATE_PLAYER_CONTROLS_SHOWN',
  'plexclients/SET_PLEX_CLIENT_TIMELINE',
  'plexclients/SET_PLEX_CLIENT_TIMELINE_COMMAND_ID',
  'plexclients/SET_COMMAND_ID',
  'plexclients/INCREMENT_COMMAND_ID',
  'synclounge/SET_USER',
  'synclounge/SET_USER_PLAYER_STATE',
  'synclounge/SET_USER_MEDIA',
  'synclounge/SET_USER_SYNC_FLEXIBILITY',
  'synclounge/DELETE_USER',
  'synclounge/ADD_MESSAGE',
]);

const persistedState = createPersistedState({
  filter: ({ type }) => !hotNonPersistedMutations.has(type),

  paths: [
    'isLibraryListView',

    'settings',

    'plex.user',
    'plex.plexAuthToken',
    'plex.clientIdentifier',
    'plex.areDevicesCached',

    'plexclients.clients',

    'plexservers.servers',
    'plexservers.lastServerId',
    'plexservers.blockedServerIds',

    'synclounge.areNotificationsEnabled',
    'synclounge.areSoundNotificationsEnabled',

    'slplayer.forceBurnSubtitles',
    'slplayer.subtitleSize',
    'slplayer.subtitlePosition',
    'slplayer.subtitleColor',
    'slplayer.streamingProtocol',
  ],
});

const store = new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  state,
  mutations,
  actions,
  getters,
  modules,
  plugins: [
    persistedState,
  ],
});

export default store;
